import * as Logger from '@thoughtspot/logger';
import { assign } from '@thoughtspot/string-util';
import { dateFormats, strings } from '@thoughtspot/translations';
import _ from 'lodash';
import { DateTime, Duration, Interval, Settings, } from 'luxon';
import moment from 'moment/min/moment-with-locales';
export const DEFAULT_QUARTER_START_MONTH = 1;
let currentLocale;
let currentTimezone = DateTime.local().zoneName || 'America/Los_Angeles';
const MINUTE_MILLIS = 60 * 1000;
let quarterStartMonth = DEFAULT_QUARTER_START_MONTH;
const logger = Logger.create('date-format');
const timeBuckets = {
    NO_BUCKET: 'ms',
    HOURLY: 'h',
    DAILY: 'd',
    WEEKLY: 'w',
    MONTHLY: 'M',
    QUARTERLY: 'Q',
    YEARLY: 'y',
    DAY_OF_WEEK: 'dow',
    DAY_OF_MONTH: 'dom',
    DAY_OF_QUARTER: 'doq',
    DAY_OF_YEAR: 'doy',
    WEEK_OF_MONTH: 'wom',
    WEEK_OF_QUARTER: 'woq',
    WEEK_OF_YEAR: 'woy',
    MONTH_OF_QUARTER: 'moq',
    MONTH_OF_YEAR: 'moy',
    QUARTER_OF_YEAR: 'qoy',
    HOUR_OF_DAY: 'hod',
};
const luxon_units = {
    [timeBuckets.NO_BUCKET]: 'millisecond',
    [timeBuckets.HOURLY]: 'hour',
    [timeBuckets.DAILY]: 'day',
    [timeBuckets.WEEKLY]: 'week',
    [timeBuckets.MONTHLY]: 'month',
    [timeBuckets.QUARTERLY]: 'quarter',
    [timeBuckets.YEARLY]: 'year',
};
const luxonAddUnits = {
    [timeBuckets.NO_BUCKET]: 'milliseconds',
    [timeBuckets.HOURLY]: 'hours',
    [timeBuckets.DAILY]: 'days',
    [timeBuckets.WEEKLY]: 'weeks',
    [timeBuckets.MONTHLY]: 'months',
    [timeBuckets.QUARTERLY]: 'quarters',
    [timeBuckets.YEARLY]: 'years',
};
const dateFormatPresets = {
    DATE_SHORT: 'DATE_SHORT',
    DATE_SHORT_WITH_HOUR: 'DATE_SHORT_WITH_HOUR',
    DATE_SHORT_WITH_HOUR_WITHOUT_YEAR: 'DATE_SHORT_WITH_HOUR_WITHOUT_YEAR',
    DATETIME_SHORT: 'DATETIME_SHORT',
    DATETIME_SHORT_WITHOUT_YEAR: 'DATETIME_SHORT_WITHOUT_YEAR',
    DATETIME_SHORT_WITH_SECONDS: 'DATETIME_SHORT_WITH_SECONDS',
    DATETIME_SHORT_WITH_MILLIS: 'DATETIME_SHORT_WITH_MILLIS',
    MONTH_WITH_YEAR: 'MONTH_WITH_YEAR',
    QUARTER_WITH_YEAR: 'QUARTER_WITH_YEAR',
    QUARTER_WITH_2_DIGIT_YEAR: 'QUARTER_WITH_2_DIGIT_YEAR',
    DEFAULT_TIME_FORMAT: 'DEFAULT_TIME_FORMAT',
    TIME_24_WITH_SECONDS: 'TIME_24_WITH_SECONDS',
    DATE_SHORT_2_DIGIT_YEAR: 'DATE_SHORT_2_DIGIT_YEAR',
    DATETIME_24_SHORT_WITHOUT_YEAR: 'DATETIME_24_SHORT_WITHOUT_YEAR',
    DATETIME_24_SHORT: 'DATETIME_24_SHORT',
    DATETIME_SHORT_WITH_SECONDS_WITHOUT_YEAR: 'DATETIME_SHORT_WITH_SECONDS_WITHOUT_YEAR',
    DATETIME_SHORT_WITH_MILLIS_WITHOUT_YEAR: 'DATETIME_SHORT_WITH_MILLIS_WITHOUT_YEAR',
    DATETIME_24_SHORT_WITH_MILLIS_WITHOUT_YEAR: 'DATETIME_24_SHORT_WITH_MILLIS_WITHOUT_YEAR',
    DATETIME_24_SHORT_WITH_MILLIS: 'DATETIME_24_SHORT_WITH_MILLIS',
    MONTH_WITH_DAY_AND_YEAR: 'MONTH_WITH_DAY_AND_YEAR',
    MONTH_WITH_2_DIGIT_YEAR: 'MONTH_WITH_2_DIGIT_YEAR',
    DAY_WITH_MONTH_NUM: 'DAY_WITH_MONTH_NUM',
    DATE_SHORT_WITH_HOUR_24_WITHOUT_YEAR: 'DATE_SHORT_WITH_HOUR_24_WITHOUT_YEAR',
    DATE_SHORT_WITH_HOUR_24: 'DATE_SHORT_WITH_HOUR_24',
    QUARTER: 'QUARTER',
    MONTH_ONLY: 'MONTH_ONLY',
    DATETIME_WITH_SHORT_OFFSET: 'DATETIME_WITH_SHORT_OFFSET',
};
const dateFormatPresetsToLuxonPresets = {
    [dateFormatPresets.TIME_24_WITH_SECONDS]: DateTime.TIME_24_WITH_SECONDS,
};
const bucketizationToDatePreset = {
    HOURLY: dateFormatPresets.DATE_SHORT_WITH_HOUR,
    DAILY: dateFormatPresets.DATE_SHORT,
    WEEKLY: dateFormatPresets.DATE_SHORT,
    MONTHLY: dateFormatPresets.MONTH_WITH_YEAR,
    QUARTERLY: dateFormatPresets.QUARTER_WITH_YEAR,
    YEARLY: 'yyyy',
};
const yearlessFormats = {
    DATE_SHORT: 'DAY_WITH_MONTH',
    DATE_SHORT_WITH_HOUR: 'DATE_SHORT_WITH_HOUR_WITHOUT_YEAR',
    DATETIME_SHORT: 'DATETIME_SHORT_WITHOUT_YEAR',
    DATETIME_SHORT_WITH_SECONDS: 'DATETIME_SHORT_WITH_SECONDS_WITHOUT_YEAR',
    DATETIME_SHORT_WITH_MILLIS: 'DATETIME_SHORT_WITH_MILLIS_WITHOUT_YEAR',
    MONTH_WITH_YEAR: 'MONTH_ONLY',
    QUARTER_WITH_YEAR: 'QUARTER',
    QUARTER_WITH_2_DIGIT_YEAR: 'QUARTER',
    DATE_SHORT_2_DIGIT_YEAR: 'DAY_WITH_MONTH',
    DATETIME_24_SHORT: 'DATETIME_24_SHORT_WITHOUT_YEAR',
    DATETIME_24_SHORT_WITH_MILLIS: 'DATETIME_24_SHORT_WITH_MILLIS_WITHOUT_YEAR',
    MONTH_WITH_DAY_AND_YEAR: 'DAY_WITH_MONTH',
    MONTH_WITH_2_DIGIT_YEAR: 'DAY_WITH_MONTH',
    DATE_SHORT_WITH_HOUR_24: 'DATE_SHORT_WITH_HOUR_24_WITHOUT_YEAR',
};
const momentOptions = {
    relativeTime: {
        dd: number => {
            if (number < 7) {
                return assign(strings.DAYS_COUNT, number);
            }
            const weeks = Math.round(number / 7);
            return weeks > 1
                ? assign(strings.WEEKS_COUNT.PLURAL, weeks)
                : assign(strings.WEEKS_COUNT.SINGULAR, weeks);
        },
    },
};
const updateMoment = async (locale) => {
    moment.locale(locale);
    moment.updateLocale(moment.locale(locale), momentOptions);
};
const getStartOfQuarter = (epochMillis) => {
    let luxonObject = DateTime.fromMillis(epochMillis);
    const adjustedMonth = luxonObject.month - (quarterStartMonth - DEFAULT_QUARTER_START_MONTH);
    const customQuarter = Math.ceil(adjustedMonth / 3);
    let customQuarterStartMonth = customQuarter * 3 -
        2 +
        (quarterStartMonth - DEFAULT_QUARTER_START_MONTH);
    if (customQuarterStartMonth < 1) {
        customQuarterStartMonth = 12 + customQuarterStartMonth;
        luxonObject = luxonObject.plus({ years: -1 });
    }
    return luxonObject
        .set({ month: customQuarterStartMonth })
        .startOf('month')
        .valueOf();
};
const getCustomStartOfYear = (epochMillis) => {
    const luxonDateObject = DateTime.fromMillis(epochMillis);
    let customYear = luxonDateObject
        .set({ month: quarterStartMonth })
        .startOf('month');
    if (customYear.valueOf() > epochMillis) {
        customYear = customYear.plus({ years: -1 });
    }
    return customYear.valueOf();
};
const useQuarterStart = (luxonDate, quarterStartMonth) => {
    luxonDate.quarterStartMonth = quarterStartMonth;
    return luxonDate;
};
const initDateFormat = async (locale) => {
    currentLocale = locale;
    Settings.defaultLocale = currentLocale;
    await updateMoment(locale);
    moment.relativeTimeThreshold('s', 60);
    moment.relativeTimeThreshold('ss', 3);
    moment.relativeTimeThreshold('m', 60);
    moment.relativeTimeThreshold('h', 24);
    moment.relativeTimeThreshold('d', 30);
    moment.relativeTimeThreshold('M', 12);
};
const actualYearProperty = Object.getOwnPropertyDescriptor(DateTime.prototype, 'year');
const actualQuarterProperty = Object.getOwnPropertyDescriptor(DateTime.prototype, 'quarter');
const setQuarterStartMonth = (start = 0) => {
    quarterStartMonth = start;
    Object.defineProperty(DateTime.prototype, 'year', {
        get() {
            const quarterStart = this.quarterStartMonth || quarterStartMonth;
            const actualYear = actualYearProperty.get.call(this);
            let fiscalYear = Number(actualYear);
            if (quarterStart > 1 && this.month >= quarterStart) {
                fiscalYear = actualYear + 1;
            }
            return fiscalYear;
        },
    });
    Object.defineProperty(DateTime.prototype, 'quarter', {
        get() {
            const quarterStart = this.quarterStartMonth || quarterStartMonth;
            let quarter = actualQuarterProperty.get.call(this);
            if (quarterStart === DEFAULT_QUARTER_START_MONTH) {
                return quarter;
            }
            quarter = Math.ceil((this.month - (quarterStart - DEFAULT_QUARTER_START_MONTH)) / 3);
            if (quarter < 1) {
                quarter += 4;
            }
            return quarter;
        },
    });
};
const weekdays = () => {
    return moment.weekdays(true);
};
const weekdaysShort = () => {
    return moment.weekdaysShort(true);
};
const weekdaysMin = () => {
    return moment.weekdaysMin(true);
};
const months = () => {
    return moment.months();
};
const monthsShort = () => {
    return moment.monthsShort();
};
const setLocale = async (locale) => {
    currentLocale = locale;
    await updateMoment(locale);
    Settings.defaultLocale = locale;
};
const setTimeZone = (timezone) => {
    currentTimezone = timezone;
    Settings.defaultZone = currentTimezone;
};
const getLocaleBasedDateFormat = (format) => {
    return dateFormats[format] || format;
};
const parseDate = (dateString, lenient, format) => {
    return DateTime.fromFormat(dateString, getLocaleBasedDateFormat(format)).toJSDate();
};
const isValidDateFromFormat = (dateString, format) => {
    if (dateString) {
        return DateTime.fromFormat(dateString, getLocaleBasedDateFormat(format))
            .isValid;
    }
    return false;
};
const getEpochForDateTime = ({ date, hour, minute }, format) => {
    return DateTime.fromFormat(date, getLocaleBasedDateFormat(format))
        .set({ hour, minute })
        .valueOf();
};
const getEpochFromDateString = (dateString, format) => {
    if (dateString) {
        return DateTime.fromFormat(dateString, getLocaleBasedDateFormat(format)).valueOf();
    }
    return -1;
};
const getStartOfField = (epochMillis, fieldName) => {
    if (fieldName === timeBuckets.QUARTERLY) {
        return getStartOfQuarter(epochMillis);
    }
    if (fieldName === timeBuckets.YEARLY && quarterStartMonth > 1) {
        return getCustomStartOfYear(epochMillis);
    }
    return DateTime.fromMillis(epochMillis)
        .startOf(luxon_units[fieldName])
        .valueOf();
};
const getEndOfField = (epochMillis, fieldName) => {
    if (fieldName === timeBuckets.QUARTERLY) {
        return DateTime.fromMillis(getStartOfQuarter(epochMillis))
            .plus({ months: 2 })
            .endOf('month')
            .valueOf();
    }
    if (fieldName === timeBuckets.YEARLY && quarterStartMonth > 1) {
        return DateTime.fromMillis(getCustomStartOfYear(epochMillis))
            .plus({ months: 11 })
            .endOf('month')
            .valueOf();
    }
    return DateTime.fromMillis(epochMillis)
        .endOf(luxon_units[fieldName])
        .valueOf();
};
const getRelativeTime = (epochMillis, withoutSuffix = false) => {
    return moment(epochMillis).fromNow(withoutSuffix);
};
const getRelativeTimeNumeric = (epochMillis, withoutSuffix = false) => {
    let relativeTime = moment(epochMillis).fromNow(withoutSuffix);
    const singularToNumericMap = {
        'a minute ago': '1 minute ago',
        'a day ago': '1 day ago',
        'an hour ago': '1 hour ago',
        'a month ago': '1 month ago',
        'a week ago': '1 week ago',
        'a year ago': '1 year ago',
    };
    relativeTime = singularToNumericMap[relativeTime] || relativeTime;
    return relativeTime;
};
const getDaysFromNow = (epochMillis, negativeAllowed = false) => {
    const duration = moment(epochMillis).diff(moment(), 'days', true);
    if (duration > 0 || negativeAllowed) {
        return `${Math.ceil(duration)}`;
    }
    return `${0}`;
};
const getTimeZoneOffset = (timestamp = Date.now(), timezone = currentTimezone, useMinutesUnit = false) => {
    timestamp = timestamp instanceof Date ? timestamp.getTime() : timestamp;
    return (DateTime.fromMillis(timestamp).setZone(timezone).offset *
        (useMinutesUnit ? 1 : MINUTE_MILLIS));
};
const addToField = (epochMillis, field, delta) => {
    if (field === timeBuckets.QUARTERLY) {
        field = timeBuckets.MONTHLY;
        delta = 3;
    }
    return DateTime.fromMillis(epochMillis)
        .plus({ [luxonAddUnits[field]]: delta })
        .valueOf();
};
const subtractFromField = (epochMillis, field, delta) => {
    if (field === timeBuckets.QUARTERLY) {
        field = timeBuckets.MONTHLY;
        delta = 3;
    }
    return DateTime.fromMillis(epochMillis)
        .minus({ [luxonAddUnits[field]]: delta })
        .valueOf();
};
const getDay = (epochMillis) => {
    return DateTime.fromMillis(epochMillis).day;
};
const getHourOfDay = (epochMillis) => {
    return DateTime.fromMillis(epochMillis).hour;
};
const getMinute = (epochMillis) => {
    return DateTime.fromMillis(epochMillis).minute;
};
const formatDateTime = (epochMillis, format, useSystemCalendar, options) => {
    let luxonDate;
    try {
        luxonDate = DateTime.fromMillis(epochMillis);
    }
    catch (e) {
        return strings.INVALID_DATE;
    }
    if (dateFormats[format]) {
        if (_.get(options, 'omitYear')) {
            if (yearlessFormats[format]) {
                format = yearlessFormats[format];
            }
        }
        format = dateFormats[format];
    }
    const customCalendarOverridesFiscalOffset = _.get(options, 'customCalendarOverridesFiscalOffset');
    if (!format || dateFormatPresetsToLuxonPresets[format]) {
        return luxonDate.toLocaleString(dateFormatPresetsToLuxonPresets[format]);
    }
    format = format.replace(/qqq/, "'Q'q");
    format = format.replace(/(YYYY|YY)/, _.lowerCase);
    if (quarterStartMonth > 1 &&
        !useSystemCalendar &&
        !customCalendarOverridesFiscalOffset) {
        format = format.replace(/[yyyy||yy]/, "'FY' $&");
    }
    const isGregorianCal = Boolean(options?.isGregorianCalendar);
    return useQuarterStart(luxonDate, useSystemCalendar || isGregorianCal
        ? DEFAULT_QUARTER_START_MONTH
        : quarterStartMonth).toFormat(format);
};
const getFormattedDateTimeByFormatAndLocale = (epochMillis, format, locale) => DateTime.fromMillis(epochMillis, { locale }).toFormat(format);
const getDurationObject = (millis, options) => {
    return Duration.fromMillis(millis, options);
};
const getIntersectionRange = (date1Start, date1End, date2Start, date2End) => {
    return Interval.fromDateTimes(DateTime.fromMillis(date1Start), DateTime.fromMillis(date1End)).intersection(Interval.fromDateTimes(DateTime.fromMillis(date2Start), DateTime.fromMillis(date2End)));
};
const isIntervalValid = (interval) => {
    return interval.isValid;
};
const intervalToJSDateArray = (interval) => {
    return [interval.start.toJSDate(), interval.end.toJSDate()];
};
const getFormattedDateTimeByFormat = (value, format) => DateTime.fromMillis(value).toFormat(format);
const getFormattedDateTimeByPreset = (value, preset) => {
    const presetFormat = dateFormatPresets[preset] ?? dateFormatPresets.DATE_SHORT;
    const dateFormat = dateFormats[presetFormat];
    return getFormattedDateTimeByFormat(value, dateFormat);
};
const isValidDateFormat = format => {
    return !!DateTime.utc().toFormat(format);
};
const addHoursToTime = (startTime, duration) => {
    return moment(startTime, 'h:mm a')
        .add(duration, 'hours')
        .format('h:mm a');
};
const convertMillisecondsToLocalTimeZone = (milliseconds) => {
    const clusterTimeZoneOffset = getTimeZoneOffset(milliseconds);
    const localTimeZoneOffset = new Date(milliseconds).getTimezoneOffset() * MINUTE_MILLIS;
    const millisecondsInUTC = milliseconds + clusterTimeZoneOffset + localTimeZoneOffset;
    return millisecondsInUTC;
};
const covertTimestampToDate = (timestamp) => {
    return moment(timestamp).format('L');
};
const getParsedDate = (date) => {
    const offset = new Date().getTimezoneOffset() * 60 * 1000;
    const timestamp = Date.parse(date?.replace(/\s+/g, 'T'));
    return new Date(timestamp).getTime() - offset;
};
const getTimeDateByTimeStamp = (timeStamp, timeZone) => {
    const object = new Date(timeStamp);
    const time = object.toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        ...(timeZone && { timeZone }),
    });
    const date = moment(timeStamp).format('MM/DD/YYYY');
    return `${time}, ${date}`;
};
const getMonthDayByTimeStamp = (timeStamp) => {
    const monthDay = moment(timeStamp).format('MMMM Do');
    return monthDay;
};
const getTimeZoneAbbreviation = (timeZone = currentTimezone, timeZoneName = 'short') => {
    try {
        const timeZoneAbbreviation = new Date()
            .toLocaleTimeString('en-us', {
            timeZone,
            timeZoneName,
        })
            .split(' ')
            .splice(2)
            .join(' ');
        return timeZoneAbbreviation;
    }
    catch (e) {
        logger.error(e);
    }
    return '';
};
const getNextDateTimeByBucket = (epochMillis, bucket, useSystemCalendar = true) => {
    let luxonDate;
    try {
        luxonDate = DateTime.fromMillis(epochMillis);
    }
    catch (e) {
        return strings.INVALID_DATE;
    }
    return useQuarterStart(luxonDate, useSystemCalendar ? DEFAULT_QUARTER_START_MONTH : quarterStartMonth)
        .plus({ [bucket]: 1 })
        .toMillis();
};
const formatEpochMillisToFiscalYear = (epochMillis, bucket = 'd', dateFormat, useSystemCalendar = true) => {
    let luxonDate;
    try {
        luxonDate = DateTime.fromMillis(epochMillis);
    }
    catch (e) {
        return strings.INVALID_DATE;
    }
    let format = "MM/dd/'FY' yyyy";
    if (dateFormat && dateFormat.startsWith('d')) {
        format = "dd/MM/'FY' yyyy";
    }
    switch (bucket) {
        case timeBuckets.MONTHLY:
            format = "MMM 'FY' yyyy";
            break;
        case timeBuckets.QUARTERLY:
            format = "'Q'q 'FY' yyyy";
            break;
        case timeBuckets.YEARLY:
            format = "'FY' yyyy";
            break;
    }
    const adjustedDate = useQuarterStart(luxonDate, useSystemCalendar ? DEFAULT_QUARTER_START_MONTH : quarterStartMonth);
    return adjustedDate.toFormat(format);
};
const getUtcMillisFromTimeInTimeZone = (year, month, day, hour, minute, timezone) => {
    const dateTime = DateTime.fromObject({
        year,
        month: month + 1,
        day,
        hour,
        minute,
    }, { zone: timezone });
    return dateTime.toMillis();
};
const getFormatedDateWithTimezone = (epochMillis) => {
    const result = `${formatDateTime(epochMillis, 'd MMMM, HH:mm')} ${DateTime.fromMillis(epochMillis).zoneName}`;
    return result;
};
export { bucketizationToDatePreset, timeBuckets, dateFormatPresets, initDateFormat, setQuarterStartMonth, setLocale, setTimeZone, parseDate, isValidDateFromFormat, getEpochForDateTime, getEndOfField, getStartOfField, getRelativeTime, getRelativeTimeNumeric, getTimeZoneOffset, getTimeZoneAbbreviation, addToField, subtractFromField, getDay, getHourOfDay, getMinute, formatDateTime, getFormattedDateTimeByFormatAndLocale, getDurationObject, months, monthsShort, getEpochFromDateString, getIntersectionRange, isIntervalValid, intervalToJSDateArray, weekdays, weekdaysMin, weekdaysShort, getFormattedDateTimeByFormat, getFormattedDateTimeByPreset, isValidDateFormat, addHoursToTime, getDaysFromNow, convertMillisecondsToLocalTimeZone, covertTimestampToDate, getParsedDate, getTimeDateByTimeStamp, getMonthDayByTimeStamp, getNextDateTimeByBucket, formatEpochMillisToFiscalYear, getLocaleBasedDateFormat, getUtcMillisFromTimeInTimeZone, luxonAddUnits, getFormatedDateWithTimezone, };
//# sourceMappingURL=date-format.js.map